import clsx from "clsx";
import { FunctionComponent } from "react";
import { RenderWhen } from "../../../../../components";
import cardStyles from "../../../../../components/subheader/card/card.module.scss";
import { enums } from "../../../../../shim/jsbridge";
import {
  getMenuItemForPlatform,
  isLocaleAvailableForPlatform
} from "../../../../../utils";
import { AnalysisTypeInfo } from "../../../../header/hooks/usePrepareReanalyzeData";
import GeolocationMenuItem, {
  GEOLOCATION_TO_LABEL
} from "../../geolocationmenuitem/GeolocationMenuItem";
import ReanalyzeCardContainer from "../reanalyze-card-container/ReanalyzeCardContainer";
import ReanalyzeDropdown from "../reanalyze-dropdown/ReanalyzeDropdown";
import ReanalyzeFormInput, {
  LAYOUT_TYPE
} from "../reanalyze-form-input/ReanalyzeFormInput";
import {
  DEFAULT_MENU_STYLES,
  RlcsErrors,
  RlcsParams
} from "../reanalyze-form/";
import ReanalyzeSwitchInput from "../reanalyze-switch-input/ReanalyzeSwitchInput";
import ReanalyzeTextInput from "../reanalyze-text-input/ReanalyzeTextInput";
import { useRlcsQuotaLimitInfo } from "../../../hooks";
import RlcsQuotaLimitInfo from "./RlcsQuotaLimitInfo";

type RLCSParamValue = string | number | boolean | undefined;
export type RLCSParamChangeValue = Record<string, RLCSParamValue>;

interface RlcsConfigurationInputProps {
  isFileAnalysis: boolean;
  isSelected: boolean;
  analysisInfo?: AnalysisTypeInfo;
  params: RlcsParams;
  disabled?: boolean;
  errors?: RlcsErrors;
  onParamChange: (value: RLCSParamChangeValue) => void;
  onSelect: () => void;
}

const PLATFORMS_SUPPORTED_BY_DYNAMIC_ANALYSIS = [
  enums.platforms.WINDOWS_10,
  enums.platforms.WINDOWS_11,
  enums.platforms.WINDOWS_7,
  enums.platforms.MACOS_11,
  enums.platforms.UBUNTU_20
];

const filterByAnalysisMode = (platform: string, isFileAnalysis: boolean) => {
  if (isFileAnalysis) {
    return true;
  }

  return PLATFORMS_SUPPORTED_BY_DYNAMIC_ANALYSIS.includes(platform);
};

const RlcsConfigurationInput: FunctionComponent<
  RlcsConfigurationInputProps
> = ({
  isFileAnalysis,
  isSelected,
  params,
  errors,
  analysisInfo,
  disabled,
  onSelect,
  onParamChange
}) => {
  const { isHighUsage, isMaxedOut, displayPercent, showLimitInfo } =
    useRlcsQuotaLimitInfo();

  const inputDisabled =
    !analysisInfo?.enabled ||
    disabled ||
    analysisInfo.queue_filled ||
    analysisInfo?.is_loading_analysis_status;

  const shouldShowTooltip =
    inputDisabled && !!analysisInfo?.additional_information;

  const helpText =
    analysisInfo?.additional_information ??
    (!analysisInfo?.last_completed_on
      ? analysisInfo?.display_status
      : undefined);

  const internetSimulationDisabled =
    inputDisabled ||
    !isFileAnalysis ||
    params.platform === enums.platforms.MACOS_11 ||
    params.platform === enums.platforms.ANDROID_12;

  const timeoutDisabled =
    inputDisabled || params.platform === enums.platforms.ANDROID_12;

  return (
    <ReanalyzeCardContainer
      classNameInnerContainer={clsx("p-0 border-1", "mb-10")}
      disabled={inputDisabled}
      selected={isSelected}
      hoverable
      layout={LAYOUT_TYPE.NONE}
      tooltipMessage={
        shouldShowTooltip && !showLimitInfo
          ? analysisInfo?.additional_information
          : undefined
      }
      classNameOuterContainer={"mb-10"}
    >
      <div className={"w-100"}>
        <div
          className={clsx(
            "py-15 px-20",
            inputDisabled ? "cursor-not-allowed" : "cursor-pointer",
            cardStyles.card_hoverable
          )}
        >
          <div
            onClick={() => {
              if (inputDisabled) {
                return;
              }
              onSelect();
            }}
          >
            <ReanalyzeFormInput
              name="rl_dynamic_analysis"
              label="Cloud Sandbox"
              labelIcon="rl-logo-square"
              labelIconSize={18}
              helpImportant={shouldShowTooltip ? undefined : helpText}
              timestamp={analysisInfo?.last_completed_on}
              disabled={inputDisabled}
              layout={LAYOUT_TYPE.ROW}
              checked={isSelected}
              isLoadingAnalysisStatus={analysisInfo?.is_loading_analysis_status}
              trailingLabelContent={
                showLimitInfo ? (
                  <RlcsQuotaLimitInfo
                    isHighUsage={isHighUsage}
                    isMaxedOut={isMaxedOut}
                    displayPercent={displayPercent}
                  />
                ) : undefined
              }
            />
          </div>
        </div>
        <></>
        <RenderWhen condition={isSelected}>
          <div className="d-flex flex-column border-top border-primary">
            <div className="d-flex flex-row border-top">
              <ReanalyzeDropdown<string>
                filterValue={params.platform}
                fieldName="PLATFORM"
                className="flex-grow-1 w-100"
                options={Object.entries(enums.platforms)
                  .filter(([, platform]) =>
                    filterByAnalysisMode(platform as string, isFileAnalysis)
                  )
                  .map(([key, value]) => getMenuItemForPlatform(key, value))}
                filterTitle="platform"
                menuStyles={DEFAULT_MENU_STYLES}
                onFilterChange={({ value }) => {
                  onParamChange({ platform: value });
                }}
                disabled={inputDisabled}
              />
              <ReanalyzeDropdown<string>
                filterValue={params.locale}
                filterTitle="locale"
                className="flex-grow-1 w-100 border-end border-start border-color-gray-84"
                options={Object.values(enums.locales).map(value => ({
                  label: value as string,
                  value: value as string
                }))}
                fieldName="locale"
                menuStyles={DEFAULT_MENU_STYLES}
                onFilterChange={({ value }) => {
                  onParamChange({ locale: value });
                }}
                disabled={
                  !isLocaleAvailableForPlatform(params.platform) ||
                  inputDisabled
                }
              />
              <ReanalyzeDropdown<string>
                filterValue={params.geolocation}
                filterTitle="geolocation"
                className="flex-grow-1 w-100"
                options={Object.values(enums.geolocations).map(value => ({
                  label: GEOLOCATION_TO_LABEL[value as string],
                  value: value as string
                }))}
                fieldName="geolocation"
                menuStyles={DEFAULT_MENU_STYLES}
                onFilterChange={({ value }) => {
                  onParamChange({ geolocation: value });
                }}
                formatLabel={menuItem => (
                  <GeolocationMenuItem value={menuItem?.value} />
                )}
                disabled={inputDisabled}
              />
            </div>
            <div className="d-flex flex-row border-top">
              <ReanalyzeTextInput
                value={params.execution_timeout}
                onChange={value => {
                  const timeout = value ? parseInt(value, 10) : undefined;
                  onParamChange({ execution_timeout: timeout });
                }}
                disabled={timeoutDisabled}
                className="flex-grow-1 w-100"
                fieldLabel="Execution Timeout"
                inputLabel="seconds"
                type="number"
                placeholder="200"
                tooltip="Enter a value between 30s and 500s. Default timeout value will be used if not provided (200s)."
                error={errors?.execution_timeout}
              />
              <ReanalyzeTextInput
                value={params.sample_name ?? ""}
                onChange={value => onParamChange?.({ sample_name: value })}
                disabled={inputDisabled || !isFileAnalysis}
                className="flex-grow-1 w-100 border-end border-start"
                fieldLabel="File name"
                type="text"
                placeholder="Let the system decide"
                tooltip="Smart Sample Naming algorithm will be used if not provided."
              />
              <ReanalyzeSwitchInput
                disabled={internetSimulationDisabled}
                value={params.internet_simulation}
                onToggle={() =>
                  onParamChange?.({
                    internet_simulation: !params.internet_simulation
                  })
                }
                label="Internet Simulation"
                tooltip="Run dynamic analysis using a simulated network environment."
              />
            </div>
          </div>
        </RenderWhen>
      </div>
    </ReanalyzeCardContainer>
  );
};

export default RlcsConfigurationInput;
